/**
 * Cost Summary Card - Displays aggregated cost metrics for admin dashboard.
 */

import type { CostSummary } from '../../types';

interface CostSummaryCardProps {
  data: CostSummary | undefined;
  isLoading: boolean;
  error: Error | null;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(value);
}

function formatNumber(value: number): string {
  return new Intl.NumberFormat('en-US').format(value);
}

function formatDuration(seconds: number): string {
  if (seconds < 60) {
    return `${seconds.toFixed(1)}s`;
  }
  const minutes = Math.floor(seconds / 60);
  const remainingSeconds = seconds % 60;
  return `${minutes}m ${remainingSeconds.toFixed(0)}s`;
}

function StatCard({
  label,
  value,
  subtext,
  className = '',
}: {
  label: string;
  value: string;
  subtext?: string;
  className?: string;
}) {
  return (
    <div className={`rounded-lg border border-gray-200 bg-white p-4 ${className}`}>
      <p className="text-sm font-medium text-gray-500">{label}</p>
      <p className="mt-1 text-2xl font-semibold text-gray-900">{value}</p>
      {subtext && <p className="mt-1 text-xs text-gray-400">{subtext}</p>}
    </div>
  );
}

export function CostSummaryCard({ data, isLoading, error }: CostSummaryCardProps) {
  if (error) {
    return (
      <div className="rounded-lg border border-red-200 bg-red-50 p-4">
        <p className="text-sm text-red-700">Failed to load cost summary: {error.message}</p>
      </div>
    );
  }

  if (isLoading || !data) {
    return (
      <div className="space-y-4">
        <h3 className="text-lg font-semibold text-gray-900">Cost Summary</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="rounded-lg border border-gray-200 bg-white p-4 animate-pulse">
              <div className="h-4 bg-gray-200 rounded w-20 mb-2" />
              <div className="h-8 bg-gray-200 rounded w-24" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  const periodStart = new Date(data.period_start).toLocaleDateString();
  const periodEnd = new Date(data.period_end).toLocaleDateString();

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold text-gray-900">Cost Summary</h3>
        <span className="text-xs text-gray-500">
          {periodStart} - {periodEnd}
        </span>
      </div>

      {/* Primary metrics */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard
          label="Total Cost"
          value={formatCurrency(data.total_cost_usd)}
          subtext={`${formatNumber(data.total_api_calls)} API calls`}
          className="border-l-4 border-l-blue-500"
        />
        <StatCard
          label="KB Builds"
          value={String(data.total_builds)}
          subtext={`Avg ${formatCurrency(data.avg_build_cost_usd)}/build`}
        />
        <StatCard
          label="Refreshes"
          value={String(data.total_refreshes)}
          subtext={`Avg ${formatCurrency(data.avg_refresh_cost_usd)}/refresh`}
        />
        <StatCard
          label="Anthropic Tokens"
          value={formatNumber(data.total_anthropic_tokens)}
          subtext="Input + Output"
        />
      </div>

      {/* Timing metrics */}
      <div className="grid grid-cols-2 gap-4">
        <StatCard
          label="Avg Build Duration"
          value={formatDuration(data.avg_build_duration_seconds)}
        />
        <StatCard
          label="Avg Refresh Duration"
          value={formatDuration(data.avg_refresh_duration_seconds)}
        />
      </div>
    </div>
  );
}

export default CostSummaryCard;
